import { Button, Field, Input, Notice } from '@didto/ui';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useSession } from '@/app/session';
import { enrollTerminal } from '@/lib/api';
import { useDeviceStore } from '@/lib/deviceStore';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import { AuthFrame } from './AuthFrame';
import './auth.css';

/** Tablet enrolment: a one-time code from Devices becomes this device's identity. */
export function EnrollPage() {
  const s = useSession();
  const nav = useNavigate();
  const device = useDeviceStore();
  // ADR-037: enrolling needs the server; the connection banner above says why it is paused
  const online = useOnline();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (s.ready && s.authed && s.me?.principal === 'device') return <Navigate to="/lock" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (s.authed) await supabase.auth.signOut();
      const r = await enrollTerminal(code.replace(/\s+/g, '').toUpperCase());
      if (!r.ok || !r.session || !r.terminal) {
        setError(
          r.code === 'invalid_code'
            ? 'That code is not valid or has expired. Ask an administrator for a new one.'
            : 'Enrolment failed. Please try again.',
        );
        return;
      }
      const { error: err } = await supabase.auth.setSession({
        access_token: r.session.access_token,
        refresh_token: r.session.refresh_token,
      });
      if (err) {
        setError(errorText(err));
        return;
      }
      device.setTerminal({
        id: r.terminal.id,
        name: r.terminal.name,
        code: r.terminal.code,
        kind: r.terminal.kind as never,
        location_id: r.terminal.location_id,
        landing: r.terminal.landing,
      });
      device.setSession(null, null, null);
      await s.refresh();
      nav('/lock', { replace: true });
    } catch (x) {
      setError(errorText(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title="Enrol this device" subtitle="Enter the one-time code shown in Devices">
      <form onSubmit={(e) => void submit(e)} className="d-stack">
        <Field label="Enrolment code">
          {(id) => (
            <Input
              id={id}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              placeholder="ABCD2345"
              className="signin__code"
              data-testid="enroll-code"
            />
          )}
        </Field>
        {error && (
          <Notice tone="danger" role="alert" testId="enroll-error">
            {error}
          </Notice>
        )}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          busy={busy}
          disabled={!online || code.trim().length < 6}
          data-testid="enroll-submit"
        >
          Enrol
        </Button>
        <p className="d-help signin__note">
          Administrators <Link to="/login">sign in</Link> instead.
        </p>
      </form>
    </AuthFrame>
  );
}
